import {
  normalizePropertyAddress,
  samePropertyAddress,
  type NormalizedPropertyAddress,
  type PropertyAddressInput
} from "./property-value.js";
import { redactAttomSecrets } from "./attom-avm.js";

/** Stable tool identity for ATTOM owner-profile lookups. Separate from AVM forever. */
export const ATTOM_OWNER_LOOKUP_VERSION = "attom-owner-v1";
export const ATTOM_OWNER_INPUT_VERSION = "attom-owner-input-v1";
export const ATTOM_OWNER_POLICY_VERSION = "attom-owner-policy-v1";

/**
 * Labels the ATTOM owner adapter in evidence only. Cache identity is the
 * normalized address plus the ATTOM owner tool id — never the AVM or Zestimate
 * cache key.
 */
export const ATTOM_OWNER_PROVIDER_VERSION = "attom-propertyapi-detailowner-v1";

export const ATTOM_OWNER_SOURCE = "ATTOM";

export const ATTOM_OWNER_DEFAULT_ENDPOINT_PATH = "/propertyapi/v1.0.0/property/detailowner";

export const ATTOM_OWNER_FAILURE_CLASSES = [
  "attom_owner_input_invalid",
  "attom_owner_not_configured",
  "attom_owner_license_not_confirmed",
  "attom_owner_upstream_unauthorized",
  "attom_owner_rate_limited",
  "attom_owner_no_match",
  "attom_owner_ambiguous_match",
  "attom_owner_address_mismatch",
  "attom_owner_schema_changed",
  "attom_owner_timeout",
  "attom_owner_upstream_unavailable",
  "attom_owner_unexpected"
] as const;

export type AttomOwnerFailureClass = (typeof ATTOM_OWNER_FAILURE_CLASSES)[number];

export const ATTOM_OWNER_RETRYABLE_FAILURE_CLASSES = new Set<AttomOwnerFailureClass>([
  "attom_owner_rate_limited",
  "attom_owner_timeout",
  "attom_owner_upstream_unavailable"
]);

export type AttomOwnerRecord = {
  /** Source spelling retained exactly as ATTOM supplied when available. */
  fullName: string;
  firstName: string | null;
  lastName: string | null;
  ownerType: string | null;
  ownershipInterest: string | null;
  corporateIndicator: string | null;
  sourceOrdinal: number;
};

export type AttomOwnerObservation = {
  requestedAddress: NormalizedPropertyAddress;
  matchedAddress: NormalizedPropertyAddress;
  attomId: string | null;
  parcelId: string | null;
  owners: AttomOwnerRecord[];
  /** ATTOM owner/tax mailing address only — never property-address fallback. */
  ownerMailingAddress: string | null;
  propertyAddressDisplay: string;
  /** Numeric total assessment only when ATTOM supplied total or permitted components. */
  totalAssessedValue: number | null;
  ownerOccupancy: string | null;
  sourceAsOf: string | null;
  retrievedAt: string;
  providerVersion: string;
  source: typeof ATTOM_OWNER_SOURCE;
  endpointPath: string;
  sourceMetadata: Record<string, unknown>;
  billableReportCount: number;
};

export type AttomOwnerMappedSuccess = {
  ok: true;
  observation: AttomOwnerObservation;
};

export type AttomOwnerMappedFailure = {
  ok: false;
  failureClass: AttomOwnerFailureClass;
  message: string;
  retryable: boolean;
  requestedAddress: NormalizedPropertyAddress | null;
  matchedAddress: NormalizedPropertyAddress | null;
  attomId: string | null;
  details?: Record<string, unknown>;
};

export type AttomOwnerMappedResult = AttomOwnerMappedSuccess | AttomOwnerMappedFailure;

export type AttomOwnerToolInput = {
  workItemId: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
};

export function isAttomOwnerRetryableFailure(failureClass: string | null | undefined): boolean {
  return failureClass != null && ATTOM_OWNER_RETRYABLE_FAILURE_CLASSES.has(failureClass as AttomOwnerFailureClass);
}

export function attomOwnerCacheKey(addressKey: string): string {
  return ["attom_owner_lookup", addressKey].join(":");
}

export function attomOwnerAddressQuery(address: NormalizedPropertyAddress): { address1: string; address2: string } {
  const address1 = [address.street, address.unit].filter(Boolean).join(" ");
  const address2 = `${address.city}, ${address.state} ${address.postalCode}`;
  return { address1, address2 };
}

export function attomOwnerOperatorMessage(failureClass: string): string {
  switch (failureClass) {
    case "attom_owner_input_invalid":
      return "Enter street, city, state, and postal code for the ATTOM owner lookup.";
    case "attom_owner_not_configured":
      return "ATTOM owner lookup is not configured on the control plane.";
    case "attom_owner_license_not_confirmed":
      return "ATTOM owner/assessment display and persistence rights are not confirmed.";
    case "attom_owner_upstream_unauthorized":
      return "ATTOM rejected the configured credential for owner lookup.";
    case "attom_owner_rate_limited":
      return "ATTOM rate-limited the owner lookup request.";
    case "attom_owner_no_match":
      return "ATTOM returned no matching property for the normalized address.";
    case "attom_owner_ambiguous_match":
      return "ATTOM returned multiple property matches; confirm the requested address.";
    case "attom_owner_address_mismatch":
      return "The ATTOM property did not match the requested normalized address.";
    case "attom_owner_schema_changed":
      return "ATTOM owner response structure was not recognized.";
    case "attom_owner_timeout":
      return "ATTOM owner lookup timed out.";
    case "attom_owner_upstream_unavailable":
      return "ATTOM owner lookup upstream was unavailable.";
    default:
      return "Unexpected ATTOM owner lookup failure.";
  }
}

/**
 * Maps a raw ATTOM owner-detail HTTP outcome into a typed publishable
 * observation or typed failure. Canonical address validation runs before any
 * owner or assessment value is publishable.
 */
export function mapAttomOwnerHttpResult(input: {
  requested: PropertyAddressInput | NormalizedPropertyAddress;
  status: number;
  body: unknown;
  retrievedAt?: string;
  providerVersion?: string;
  endpointPath?: string;
  timedOut?: boolean;
  transportError?: string | null;
  billableReportCount?: number;
}): AttomOwnerMappedResult {
  const requestedAddress = isNormalizedAddress(input.requested)
    ? input.requested
    : normalizePropertyAddress(input.requested);
  if (!requestedAddress) {
    return failure("attom_owner_input_invalid", "Property address is invalid.", false, null, null, null);
  }

  if (input.timedOut) {
    return failure("attom_owner_timeout", "ATTOM owner lookup timed out.", true, requestedAddress, null, null);
  }
  if (input.transportError) {
    return failure(
      "attom_owner_upstream_unavailable",
      "ATTOM owner lookup transport failed.",
      true,
      requestedAddress,
      null,
      null,
      { transportError: input.transportError }
    );
  }

  const status = input.status;
  if (status === 401 || status === 403) {
    return failure(
      "attom_owner_upstream_unauthorized",
      "ATTOM rejected the configured credential.",
      false,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status === 429) {
    return failure(
      "attom_owner_rate_limited",
      "ATTOM rate-limited the owner lookup request.",
      true,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status === 400) {
    const bodyRecord = asRecord(input.body);
    const msg = String(asRecord(bodyRecord?.status)?.msg || bodyRecord?.message || "");
    if (/no.?result|not found|no property|successwithoutresult/i.test(msg) || bodySaysNoResult(input.body)) {
      return failure(
        "attom_owner_no_match",
        "ATTOM returned no matching property for the normalized address.",
        false,
        requestedAddress,
        null,
        null,
        { httpStatus: status }
      );
    }
    return failure(
      "attom_owner_schema_changed",
      "ATTOM rejected the owner lookup request.",
      false,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status >= 500 || status === 0) {
    return failure(
      "attom_owner_upstream_unavailable",
      "ATTOM owner lookup upstream was unavailable.",
      true,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status < 200 || status >= 300) {
    return failure(
      "attom_owner_upstream_unavailable",
      `ATTOM owner lookup returned HTTP ${status}.`,
      status >= 500,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }

  const body = asRecord(input.body);
  if (!body) {
    return failure(
      "attom_owner_schema_changed",
      "ATTOM owner response was not a JSON object.",
      false,
      requestedAddress,
      null,
      null
    );
  }

  const statusBlock = asRecord(body.status);
  const statusMsg = String(statusBlock?.msg ?? statusBlock?.message ?? "");
  const statusCode = Number(statusBlock?.code);
  if (bodySaysNoResult(body) || /successwithoutresult|no.?result|not found/i.test(statusMsg) || statusCode === 1) {
    return failure(
      "attom_owner_no_match",
      "ATTOM returned no matching property for the normalized address.",
      false,
      requestedAddress,
      null,
      null,
      { providerStatus: redactAttomSecrets(statusBlock ?? {}) }
    );
  }

  const properties = Array.isArray(body.property)
    ? body.property
    : body.property
      ? [body.property]
      : [];
  if (properties.length === 0) {
    return failure(
      "attom_owner_no_match",
      "ATTOM returned no matching property for the normalized address.",
      false,
      requestedAddress,
      null,
      null
    );
  }
  if (properties.length > 1) {
    return failure(
      "attom_owner_ambiguous_match",
      "ATTOM returned multiple property matches; confirm the requested address.",
      false,
      requestedAddress,
      null,
      null,
      { matchCount: properties.length }
    );
  }

  const property = asRecord(properties[0]);
  if (!property) {
    return failure(
      "attom_owner_schema_changed",
      "ATTOM property payload was malformed.",
      false,
      requestedAddress,
      null,
      null
    );
  }

  const matchedAddress = normalizeAttomPropertyAddress(property, requestedAddress);
  const attomId = extractAttomId(property);
  if (!matchedAddress) {
    return failure(
      "attom_owner_schema_changed",
      "ATTOM property address could not be normalized.",
      false,
      requestedAddress,
      null,
      attomId
    );
  }
  if (!samePropertyAddress(requestedAddress, matchedAddress)) {
    return failure(
      "attom_owner_address_mismatch",
      "The ATTOM property did not match the requested normalized address.",
      false,
      requestedAddress,
      matchedAddress,
      attomId
    );
  }

  const owners = extractOwners(property);
  const ownerMailingAddress = extractOwnerMailingAddress(property);
  const parcelId = extractParcelId(property);
  const totalAssessedValue = extractTotalAssessedValue(property);
  const ownerOccupancy = extractOwnerOccupancy(property);
  const sourceAsOf = extractSourceAsOf(property);
  const retrievedAt = input.retrievedAt && !Number.isNaN(Date.parse(input.retrievedAt))
    ? input.retrievedAt
    : new Date().toISOString();
  const endpointPath = input.endpointPath ?? ATTOM_OWNER_DEFAULT_ENDPOINT_PATH;

  return {
    ok: true,
    observation: {
      requestedAddress,
      matchedAddress,
      attomId,
      parcelId,
      owners,
      ownerMailingAddress,
      propertyAddressDisplay: matchedAddress.display,
      totalAssessedValue,
      ownerOccupancy,
      sourceAsOf,
      retrievedAt,
      providerVersion: input.providerVersion ?? ATTOM_OWNER_PROVIDER_VERSION,
      source: ATTOM_OWNER_SOURCE,
      endpointPath,
      sourceMetadata: redactAttomSecrets({
        provider: "attom",
        endpoint: endpointPath.replace(/^\//, ""),
        statusCode: Number.isFinite(statusCode) ? statusCode : null,
        statusMsg: statusMsg || null,
        ownerCount: owners.length,
        mailingAddressPresent: ownerMailingAddress != null,
        assessedValuePresent: totalAssessedValue != null
      }),
      billableReportCount: input.billableReportCount ?? 1
    }
  };
}

/** Formats owner names for scalar Sheets cells without changing source spelling. */
export function formatAttomOwnerNamesScalar(owners: AttomOwnerRecord[]): string | null {
  const names = owners.map((owner) => owner.fullName.trim()).filter(Boolean);
  return names.length ? names.join("; ") : null;
}

/**
 * Display helpers for operator-facing cells. Missing values are `Not available`.
 * Zero is preserved only when the provider explicitly supplied numeric zero.
 */
export function attomOwnerDisplayValue(
  value: string | number | null | undefined,
  options: { numeric?: boolean } = {}
): string | number {
  if (value == null) return "Not available";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "Not available";
    return value;
  }
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return "Not available";
  if (options.numeric) {
    const parsed = Number(text.replace(/[$,]/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return text;
}

export function projectAttomOwnerNamesRows(owners: AttomOwnerRecord[]): Array<[string, string, string]> {
  if (!owners.length) {
    return [["Not available", "Not available", "Not available"]];
  }
  return owners.map((owner) => [
    owner.fullName || "Not available",
    owner.ownerType ?? "Not available",
    owner.ownershipInterest ?? "Not available"
  ]);
}

function extractOwners(property: Record<string, unknown>): AttomOwnerRecord[] {
  const owner = asRecord(property.owner) ?? asRecord(property.Owner) ?? {};
  const owners: AttomOwnerRecord[] = [];
  const corporate = stringOrNull(owner.corporateindicator ?? owner.corporateIndicator);

  for (let index = 1; index <= 4; index += 1) {
    const block = asRecord(owner[`owner${index}`] ?? owner[`Owner${index}`]);
    const fullFromBlock = stringOrNull(
      block?.fullname ?? block?.FullName ?? block?.name ?? block?.Name
    );
    const first = stringOrNull(block?.firstname ?? block?.firstName ?? block?.FirstName);
    const last = stringOrNull(block?.lastname ?? block?.lastName ?? block?.LastName);
    const composed = [first, last].filter(Boolean).join(" ").trim() || null;
    const fullFromTop = stringOrNull(
      owner[`ownername${index}full`]
      ?? owner[`ownerName${index}Full`]
      ?? owner[`owner${index}name`]
    );
    const fullName = fullFromBlock ?? fullFromTop ?? composed;
    if (!fullName) continue;
    owners.push({
      fullName,
      firstName: first,
      lastName: last,
      ownerType: stringOrNull(block?.type ?? block?.ownerType ?? owner.type),
      ownershipInterest: stringOrNull(block?.ownership ?? block?.interest ?? block?.ownershipInterest),
      corporateIndicator: corporate,
      sourceOrdinal: index
    });
  }

  if (!owners.length) {
    const fallback = stringOrNull(owner.owner1fullname ?? owner.ownerName ?? owner.name);
    if (fallback) {
      owners.push({
        fullName: fallback,
        firstName: null,
        lastName: null,
        ownerType: stringOrNull(owner.type),
        ownershipInterest: null,
        corporateIndicator: corporate,
        sourceOrdinal: 1
      });
    }
  }
  return owners;
}

/**
 * Maps only ATTOM owner/tax mailing fields. Never falls back to the situs /
 * property address, even when those strings are equal.
 */
function extractOwnerMailingAddress(property: Record<string, unknown>): string | null {
  const owner = asRecord(property.owner) ?? asRecord(property.Owner) ?? {};
  const oneLine = stringOrNull(
    owner.mailingaddressoneline
    ?? owner.mailingAddressOneLine
    ?? owner.mailingaddressoneLine
    ?? owner.taxmailingaddressoneline
  );
  if (oneLine) return oneLine;

  const mailing = asRecord(owner.mailingaddress)
    ?? asRecord(owner.mailingAddress)
    ?? asRecord(owner.mailingsiteabsentee)
    ?? asRecord(owner.mailing);
  if (!mailing) return null;

  const line1 = stringOrNull(mailing.line1 ?? mailing.address1 ?? mailing.street);
  const line2 = stringOrNull(mailing.line2 ?? mailing.unit);
  const city = stringOrNull(mailing.locality ?? mailing.city);
  const state = stringOrNull(mailing.countrySubd ?? mailing.state);
  const postal = stringOrNull(mailing.postal1 ?? mailing.postalCode ?? mailing.zip);
  const parts = [
    [line1, line2].filter(Boolean).join(" ").trim(),
    [city, state].filter(Boolean).join(", ").trim(),
    postal
  ].filter((part) => part && String(part).trim());
  if (!parts.length) return null;
  // Prefer "street, city, ST ZIP" when city/state present.
  if (line1 && city && state) {
    return [ [line1, line2].filter(Boolean).join(" "), `${city}, ${state}${postal ? ` ${postal}` : ""}` ]
      .filter(Boolean)
      .join(", ");
  }
  return parts.join(", ");
}

function extractParcelId(property: Record<string, unknown>): string | null {
  const identifier = asRecord(property.identifier) ?? asRecord(property.Identifier) ?? {};
  return stringOrNull(
    identifier.apn
    ?? identifier.APN
    ?? identifier.parcelNumber
    ?? identifier.parcelId
    ?? property.apn
    ?? property.parcelId
  );
}

/**
 * Publishes numeric total assessment only when ATTOM supplies the total or
 * supplies documented land+improvement components. Never substitutes market,
 * AVM, list price, tax amount, or a county value.
 */
function extractTotalAssessedValue(property: Record<string, unknown>): number | null {
  const assessment = asRecord(property.assessment) ?? asRecord(property.Assessment) ?? {};
  const assessed = asRecord(assessment.assessed) ?? asRecord(assessment.Assessed) ?? {};
  const total = finiteNumberAllowZero(
    assessed.assdttlvalue
    ?? assessed.assdTtlValue
    ?? assessed.ttlvalue
    ?? assessed.total
    ?? assessment.assessedValue
    ?? assessment.totalAssessedValue
  );
  if (total !== null) return total;

  const land = finiteNumberAllowZero(assessed.assdlandvalue ?? assessed.assdLandValue ?? assessed.land);
  const improvement = finiteNumberAllowZero(assessed.assdimprvalue ?? assessed.assdImprValue ?? assessed.improvement);
  if (land !== null && improvement !== null) return land + improvement;
  return null;
}

function extractOwnerOccupancy(property: Record<string, unknown>): string | null {
  const summary = asRecord(property.summary) ?? asRecord(property.Summary) ?? {};
  const owner = asRecord(property.owner) ?? asRecord(property.Owner) ?? {};
  return stringOrNull(
    summary.absenteeInd
    ?? summary.absenteeIndicator
    ?? owner.absenteeownerstatus
    ?? owner.absenteeOwnerStatus
    ?? owner.ownerOccupied
    ?? summary.propIndicator
  );
}

function extractSourceAsOf(property: Record<string, unknown>): string | null {
  const vintage = asRecord(property.vintage) ?? asRecord(property.Vintage) ?? {};
  const assessment = asRecord(property.assessment) ?? {};
  return stringOrNull(
    vintage.lastModified
    ?? vintage.publishDate
    ?? assessment.taxyear
    ?? assessment.taxYear
    ?? assessment.assessmentyear
    ?? assessment.assessmentYear
  );
}

function normalizeAttomPropertyAddress(
  property: Record<string, unknown>,
  fallback: NormalizedPropertyAddress
): NormalizedPropertyAddress | null {
  const address = asRecord(property.address) ?? asRecord(property.Address) ?? {};
  const rawLine1 = stringOrNull(address.line1 ?? address.address1)
    ?? stringOrNull(fallback.street);
  const oneLine = stringOrNull(address.oneLine ?? address.line2);
  const locality = stringOrNull(address.locality ?? address.city) ?? fallback.city;
  const state = stringOrNull(address.countrySubd ?? address.state) ?? fallback.state;
  const postal = stringOrNull(address.postal1 ?? address.postalCode ?? address.zip) ?? fallback.postalCode;
  const unit = stringOrNull(address.unit) ?? fallback.unit;

  // ATTOM often puts a full one-liner into line1 ("200 S ELM ST GREENSBORO, NC 27401").
  // Prefer full-address parse before treating it as a street-only field.
  for (const candidate of [rawLine1, oneLine]) {
    if (!candidate) continue;
    if (looksLikeFullPropertyAddress(candidate)) {
      const parsed = normalizePropertyAddress({ fullAddress: candidate });
      if (parsed) return parsed;
    }
  }

  const street = stripEmbeddedLocality(rawLine1, locality, state, postal) ?? rawLine1;
  const structured = normalizePropertyAddress({
    street,
    unit,
    city: locality,
    state,
    postalCode: postal
  });
  if (structured) return structured;

  if (oneLine) {
    const parsed = normalizePropertyAddress({ fullAddress: oneLine });
    if (parsed) return parsed;
  }

  if (street && postal) {
    return normalizePropertyAddress({
      street,
      unit: unit ?? undefined,
      city: fallback.city,
      state: fallback.state,
      postalCode: postal
    });
  }
  return null;
}

function looksLikeFullPropertyAddress(value: string): boolean {
  const trimmed = value.trim();
  return /^.+,\s*[A-Za-z .'-]{2,}\s*,?\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\s*$/i.test(trimmed)
    || (/\b[A-Z]{2}\s+\d{5}(?:-\d{4})?\s*$/i.test(trimmed) && trimmed.includes(","));
}

/** When line1 embeds city/state/ZIP, keep only the leading street tokens. */
function stripEmbeddedLocality(
  line1: string | null,
  city: string | null,
  state: string | null,
  postal: string | null
): string | null {
  if (!line1) return null;
  let street = line1.trim();
  if (postal) {
    const token = String(postal).toUpperCase();
    const upper = street.toUpperCase();
    const idx = upper.lastIndexOf(token);
    if (idx > 0) street = street.slice(0, idx).replace(/[\s,]+$/g, "").trim();
  }
  if (state) {
    const token = String(state).toUpperCase();
    const upper = street.toUpperCase();
    const idx = upper.lastIndexOf(token);
    if (idx > 0 && idx + token.length >= upper.length - 1) {
      street = street.slice(0, idx).replace(/[\s,]+$/g, "").trim();
    }
  }
  if (city) {
    const token = String(city).toUpperCase();
    const upper = street.toUpperCase();
    const idx = upper.lastIndexOf(token);
    if (idx > 0 && idx + token.length >= upper.length - 1) {
      street = street.slice(0, idx).replace(/[\s,]+$/g, "").trim();
    }
  }
  return street || null;
}

function extractAttomId(property: Record<string, unknown>): string | null {
  const identifier = asRecord(property.identifier) ?? asRecord(property.Identifier) ?? {};
  const raw = identifier.attomId ?? identifier.AttomId ?? identifier.Id ?? identifier.id ?? property.attomId;
  if (raw == null) return null;
  const text = String(raw).trim();
  return text && /^[A-Za-z0-9._:-]{1,120}$/.test(text) ? text : null;
}

function bodySaysNoResult(body: unknown): boolean {
  const record = asRecord(body);
  if (!record) return false;
  const status = asRecord(record.status);
  const total = Number(status?.total ?? record.total);
  if (Number.isFinite(total) && total === 0) return true;
  const msg = String(status?.msg ?? status?.message ?? "");
  return /successwithoutresult|no.?result|not found/i.test(msg);
}

function failure(
  failureClass: AttomOwnerFailureClass,
  message: string,
  retryable: boolean,
  requestedAddress: NormalizedPropertyAddress | null,
  matchedAddress: NormalizedPropertyAddress | null,
  attomId: string | null,
  details?: Record<string, unknown>
): AttomOwnerMappedFailure {
  return {
    ok: false,
    failureClass,
    message,
    retryable,
    requestedAddress,
    matchedAddress,
    attomId,
    ...(details ? { details: redactAttomSecrets(details) } : {})
  };
}

function isNormalizedAddress(value: unknown): value is NormalizedPropertyAddress {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return typeof record.comparisonKey === "string"
    && typeof record.display === "string"
    && typeof record.street === "string"
    && typeof record.city === "string"
    && typeof record.state === "string"
    && typeof record.postalCode === "string";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringOrNull(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).replace(/\s+/g, " ").trim();
  return text || null;
}

/** Accepts explicit numeric zero from the provider; rejects non-finite values. */
function finiteNumberAllowZero(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const normalized = value.replace(/[$,\s]/g, "").trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
